import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Search, Loader2, FileText, User, Newspaper, Briefcase, X } from "lucide-react";
import Avatar from "@/components/Avatar";

const FILTERS = [
  { key: "all", label: "Tout" },
  { key: "document", label: "Document" },
  { key: "person", label: "Personne" },
  { key: "news", label: "Actualité" },
  { key: "service", label: "Service" },
];

export default function IntranetSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/intranet-search", { params: { q, type } }); setRes(data); }
      catch { setRes(null); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, type]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setRes(null); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const total = res ? res.persons.length + res.documents.length + res.news.length + res.services.length : 0;

  return (
    <div ref={boxRef} className="relative" data-testid="intranet-search">
      <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2.5 shadow-lg">
        <Search className="w-4 h-4 text-slate-400 shrink-0"/>
        <input value={q} onChange={e => setQ(e.target.value)} onFocus={() => q.length >= 2 && setRes(r => r)} data-testid="intranet-search-input"
               placeholder="Rechercher une personne, un document, une actualité, un service…"
               className="flex-1 text-sm text-slate-800 outline-none bg-transparent placeholder:text-slate-400"/>
        {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400"/> :
          q && <button onClick={() => { setQ(""); setRes(null); }} data-testid="intranet-search-clear"><X className="w-4 h-4 text-slate-400 hover:text-slate-700"/></button>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="intranet-search-filters">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setType(f.key)} data-testid={`intranet-filter-${f.key}`}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    type === f.key ? "bg-white text-[#0b1b3a] border-white" : "bg-white/10 text-slate-200 border-white/20 hover:bg-white/20"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {res && (
        <div className="absolute left-0 right-0 top-[52px] z-40 bg-white text-slate-800 rounded-lg shadow-2xl border border-slate-200 max-h-[420px] overflow-y-auto" data-testid="intranet-search-results">
          {total === 0 && <div className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="intranet-search-empty">Aucun résultat pour « {q} »</div>}
          <Group title="Personnes" icon={User} items={res.persons} render={u => (
            <Row key={u.id} testid={`search-person-${u.id}`} onClick={() => navigate("/classement")}
                 left={<Avatar user={u} size={30}/>} title={u.name} sub={`${u.email}${u.sub_role ? " · " + u.sub_role : ""}`}/>
          )}/>
          <Group title="Documents" icon={FileText} items={res.documents} render={d => (
            <Row key={d.id} testid={`search-doc-${d.id}`} onClick={() => window.open(`${API}/files/${d.storage_path}`, "_blank")}
                 left={<div className="w-[30px] h-[30px] rounded-md bg-violet-100 text-violet-700 grid place-items-center"><FileText className="w-4 h-4"/></div>}
                 title={d.original_filename} sub={new Date(d.created_at).toLocaleDateString("fr-FR")}/>
          )}/>
          <Group title="Actualités" icon={Newspaper} items={res.news} render={n => (
            <Row key={n.id} testid={`search-news-${n.id}`} onClick={() => navigate("/journal?section=actualites")}
                 left={<div className="w-[30px] h-[30px] rounded-md bg-orange-100 text-orange-700 grid place-items-center"><Newspaper className="w-4 h-4"/></div>}
                 title={n.title} sub={n.category || n.summary || ""}/>
          )}/>
          <Group title="Services" icon={Briefcase} items={res.services} render={s => (
            <Row key={s.key} testid={`search-service-${s.key}`} onClick={() => navigate(s.link)}
                 left={<div className="w-[30px] h-[30px] rounded-md bg-blue-100 text-blue-700 grid place-items-center"><Briefcase className="w-4 h-4"/></div>}
                 title={s.label} sub={s.desc}/>
          )}/>
        </div>
      )}
    </div>
  );
}

function Group({ title, icon: Icon, items, render }) {
  if (!items?.length) return null;
  return (
    <div className="py-2">
      <div className="px-4 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><Icon className="w-3 h-3"/>{title}</div>
      {items.map(render)}
    </div>
  );
}

function Row({ left, title, sub, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-slate-50 transition-colors">
      {left}
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-800 truncate">{title}</div>
        {sub && <div className="text-xs text-slate-500 truncate">{sub}</div>}
      </div>
    </button>
  );
}
